import { useEffect, useState } from "react";
import { RescueRnTable } from "../../../components/designs/rescueRn-table";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import { BookOpen, Building2, Users } from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import { MetricsCard } from "../../../components/designs/matrics-card";
import { SearchAndFiltersListing } from "../../../components/search-filter-listing";
import moment from "moment";
import { ActionDropdown } from "../../../components/designs/action-dropdown";
import axiosService from "../../../core/services/axios-service";
import { getInitials } from "../../../core/services/global-functions";
import ToastService from "../../../core/services/toaster-service";

export default function LeadsManagement() {
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  // Define actions for the ActionDropdown

  const columns = [
    {
      name: "Full Name",
      selector: (row) => row.full_name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <div className="organization-avatar me-3">
            {getInitials(row.full_name)}
          </div>
          {row.full_name}
        </div>
      ),
      width: "250px",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.email}
        </span>
      ),
    },
    {
      name: "Contact Number",
      selector: (row) => row.contact_number,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.contact_number}
        </span>
      ),
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.createdAt
            ? moment(row.createdAt).format("Do MMM YYYY, h:mm A")
            : ""}
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.isNew,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.isNew ? "New" : "Existing"}
        </span>
      ),
    },

    {
      name: "Actions",
      cell: (row) => (
        <ActionDropdown
          rowData={row}
          actions={getRowAction(row)}
          actionDropClicked={(data) => actionDropClicked(data)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];
  const getRowAction = (row) => {
    let returnArr = [];
    if(row.isNew === true) {
      returnArr.push({
      id: "mark_as_contacted",
      label: "Mark as contacted",
      icon: BookOpen,
      value: 'mark_as_contacted',
    });
    } else {
      returnArr.push({
      id: "mark_as_new",
      label: "Mark as New",
      icon: BookOpen,
      value: 'mark_as_new',
    });
    }
    return returnArr;
  }

  const fetchData = async (
    page = 1,
    size = 10,
    sort = "createdAt",
    order = "asc",
    search = ""
  ) => {
    setLoading(true);
    const res = await axiosService.get(ApiRoutes.LEAD_LISTING, {
      params: {
        perPage: size,
        currentPage: page,
        sortBy: sort,
        sortOrder: order,
        searchStr: search || "",
      },
    });
    setData(res.data.data || []);
    setTotalRows(res.data.total || 0);
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400); // 400ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchData(currentPage, perPage, sortBy, sortOrder, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sortBy, sortOrder, debouncedSearch]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };
  const handleSort = (column, sortDirection) => {
    setSortBy(column.sortField || "createdAt");
    setSortOrder(sortDirection);
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const actionDropClicked = (data) => {
    if (data.action === "mark_as_contacted" || data.action === "mark_as_new") {
      changeStatus(data.rowData._id, data.rowData.isNew); 
    }
  };
  const changeStatus = (id, isNew) => {
    
    axiosService
      .put(ApiRoutes.UPDATE_LEAD_STATUS + "/" + id, {
        isNew: !isNew,
      })
      .then((res) => {
        if (res.data && res?.data?.message?.toUpperCase() === "CONTACT UPDATED.") {
          ToastService.success('Lead status updated successfully.');
          fetchData(currentPage, perPage, sortBy, sortOrder, debouncedSearch);

        } else {
          ToastService.error("Failed to change status. Please try again.");
        }
      })
      .catch(() => ToastService.error("Server error. Please try again."));
  };
  // Function to get count of new leads in the last month
  const getNewLeadsCount = (data) => {
    // Get the first and last day of the previous calendar month
    const startOfLastMonth = moment().subtract(1, "months").startOf("month");
    const endOfLastMonth = moment().subtract(1, "months").endOf("month");
    return data.filter((org) =>
      moment(org.createdAt).isBetween(
        startOfLastMonth,
        endOfLastMonth,
        null,
        "[]"
      )
    ).length;
  };
  return (
    <>
      <ListingHeader
        title={"Leads Management"}
        breadCrumb={[
          { label: "Dashboard", href: "/admin/dashboard" },
          { label: "Leads", href: "/admin/leads" },
        ]}
        actions={[]}
      />

      <div className="flex-grow-1 p-4">
        <div className="container-fluid p-0">
          <div className="row g-4 mb-4">
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Leads"
                value={loading ? "--" : totalRows}
                icon={Building2}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="New Leads This Month"
                value={
                  loading ? "--" : totalRows - getNewLeadsCount(data)
                }
                icon={Users}
              />
            </div>
          </div>
          <div className="mb-4">
            <SearchAndFiltersListing
              placeholder="Search Leads..."
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          </div>
          <RescueRnTable
            data={data}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            loadingTableText="Loading organizations..."
            handleSort={handleSort}
            onChangePage={(page) => handlePageChange(page)}
            handlePerRowsChange={(newPerPage) =>
              handlePerRowsChange(newPerPage)
            }
            perPage={perPage}
          />
        </div>
      </div>
      {/* End of the above code */}
    </>
  );
}
